import { EMPTY_OBJECT } from '@unifyapps/carbon/consts/empty';
import type { ThemeEntityColorScheme } from '@unifyapps/defs/theme/types';
import { extractCssVars } from './extractCssVars';

interface ResolveArgs {
  cssVars: Record<string, unknown>;
  defaultScheme: ThemeEntityColorScheme;
}

interface ResolvedTokens {
  light: Record<string, string>;
  dark: Record<string, string>;
}

/**
 * Normalized { light, dark } from any legacy storage shape.
 *
 * Rules (priority order):
 *  1. both populated → as-is.
 *  2. scheme='dark' + flat only → mirror flat to both. theme-manager dark legacy: flat = dark tokens.
 *  3. scheme='dark' + nest only → mirror dark to both. themeGenerator dark legacy.
 *  4. flat only, other schemes → mirror flat to dark. light-only + 'both' with empty dark legacy.
 *  5. nest only, other schemes → mirror dark to flat. data anomaly heal; avoids default.css fallback.
 *  6. both empty → empty. caller falls back to default.css.
 *
 * 2/3 precede 4/5 so theme-manager's flat=dark convention (when scheme='dark')
 * is honored before generic mirror logic.
 */
export function resolveThemeTokens({ cssVars, defaultScheme }: ResolveArgs): ResolvedTokens {
  const { light, dark } = extractCssVars(cssVars);
  const hasLight = Object.keys(light).length > 0;
  const hasDark = Object.keys(dark).length > 0;

  // rule 1
  if (hasLight && hasDark) {
    return { light, dark };
  }

  // rule 2
  if (defaultScheme === 'dark' && hasLight && !hasDark) {
    return { light, dark: light };
  }

  // rule 3
  if (defaultScheme === 'dark' && !hasLight && hasDark) {
    return { light: dark, dark };
  }

  // rule 4
  if (hasLight && !hasDark) {
    return { light, dark: light };
  }

  // rule 5
  if (!hasLight && hasDark) {
    return { light: dark, dark };
  }

  // rule 6: both empty
  return { light: EMPTY_OBJECT, dark: EMPTY_OBJECT };
}
